import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { BaseCoreState } from '../reducers';
import { baseAbsenceRequestsActions, baseSharedActions } from '../actions';
import { baseAuthSelectors } from '../selectors';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { BaseListPagination } from '../../shared/enums/base-list-pagination.enum';
import { BaseToastAlertClasses } from '../../shared/enums/base-toast-alert-classes.enum';
import { CandidateProfileService } from '../../../api';

type AbsenceRequestStatusParam = NonNullable<Parameters<CandidateProfileService['getPspAbsenceList']>[0]['statuses[]']>;
type AbsenceRequestTypeParam = NonNullable<Parameters<CandidateProfileService['getPspAbsenceList']>[0]['types[]']>;

/** Options shown in the employee typeahead per search; the user narrows the list by typing. */
export const EMPLOYEE_OPTIONS_PAGE_SIZE = 30;
/** Rows shown on the dashboard "employee activity" tile. */
export const DASHBOARD_ABSENCE_REQUESTS_SIZE = 3;

@Injectable()
export class BaseAbsenceRequestsEffects {
  loadAbsenceRequests$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAbsenceRequestsActions.loadAbsenceRequests),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const { page, size, statuses, types, profiles, pspBranches } = action.payload;

        return this.candidateProfileService
          .getPspAbsenceList({
            psp: user.organizationId,
            page: page || BaseListPagination.page,
            size: size || BaseListPagination.size,
            'statuses[]': this.toArrayParam(statuses) as AbsenceRequestStatusParam | undefined,
            'types[]': this.toArrayParam(types) as AbsenceRequestTypeParam | undefined,
            'profiles[]': this.toArrayParam(profiles) as unknown as number[] | undefined,
            'pspBranches[]': this.toArrayParam(pspBranches) as unknown as number[] | undefined,
          })
          .pipe(
            mergeMap((response) => [
              baseAbsenceRequestsActions.setAbsenceRequests({ payload: response }),
              baseAbsenceRequestsActions.successAbsenceRequests(),
            ]),
            catchError(() => of(baseAbsenceRequestsActions.errorAbsenceRequests()))
          );
      })
    );
  });

  loadDashboardAbsenceRequests$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAbsenceRequestsActions.loadDashboardAbsenceRequests),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([, user]) => {
        return this.candidateProfileService
          .getPspAbsenceList({
            psp: user.organizationId,
            page: BaseListPagination.page,
            size: DASHBOARD_ABSENCE_REQUESTS_SIZE,
            'statuses[]': ['pending'],
          })
          .pipe(
            mergeMap((response) => [
              baseAbsenceRequestsActions.setDashboardAbsenceRequests({ payload: response }),
              baseAbsenceRequestsActions.successDashboardAbsenceRequests(),
            ]),
            catchError(() => of(baseAbsenceRequestsActions.errorDashboardAbsenceRequests()))
          );
      })
    );
  });

  loadEmployees$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAbsenceRequestsActions.loadEmployees),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.candidateProfileService
          .getAppApiWebV1PspProfileListIndex({
            psp: user.organizationId,
            page: BaseListPagination.page,
            size: EMPLOYEE_OPTIONS_PAGE_SIZE,
            active: true,
            search: action.payload.search || undefined,
          })
          .pipe(
            mergeMap((response) => [
              baseAbsenceRequestsActions.setEmployees({ payload: response.data || [] }),
              baseAbsenceRequestsActions.successEmployees(),
            ]),
            catchError(() => of(baseAbsenceRequestsActions.errorEmployees()))
          );
      })
    );
  });

  /** Errors (403/404/409) surface through the global HTTP error interceptor toast; the row stays unchanged. */
  approveAbsenceRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAbsenceRequestsActions.approveAbsenceRequest),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.candidateProfileService
          .putPspAbsenceApprove({ psp: user.organizationId, absence: action.payload.absence })
          .pipe(
            mergeMap(() => [
              baseAbsenceRequestsActions.successApproveAbsenceRequest(),
              baseSharedActions.addSystemAlert({
                payload: { class: BaseToastAlertClasses.success, body: 'absenceRequests.approved' },
              }),
            ]),
            catchError(() => of(baseAbsenceRequestsActions.errorApproveAbsenceRequest()))
          );
      })
    );
  });

  declineAbsenceRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseAbsenceRequestsActions.declineAbsenceRequest),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.candidateProfileService
          .putPspAbsenceDecline({
            psp: user.organizationId,
            absence: action.payload.absence,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => [
              baseAbsenceRequestsActions.successDeclineAbsenceRequest(),
              baseSharedActions.addSystemAlert({
                payload: { class: BaseToastAlertClasses.success, body: 'absenceRequests.declined' },
              }),
            ]),
            catchError(() => of(baseAbsenceRequestsActions.errorDeclineAbsenceRequest()))
          );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store<BaseCoreState>,
    private candidateProfileService: CandidateProfileService
  ) {}

  /**
   * The backend reads only the first element of an `x[]` query param and splits it by comma
   * (`AbstractApiController::getQueryContent`), so a multi-value filter has to travel as a single
   * comma-joined value (`statuses[]=pending,approved`), the same way `BaseFilterService` sends it.
   * Repeated params (`statuses[]=pending&statuses[]=approved`) would silently keep the first value only.
   */
  private toArrayParam(value?: string): string[] | undefined {
    return value ? [value] : undefined;
  }
}
